import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { IconPlus } from "../../../components/icons";
import { Button } from "../../../components/ui/button";
import { useAuth } from "../../../contexts/AuthProvider";
import { useAdminWorkspace } from "../../../contexts/AdminWorkspaceProvider";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import {
  FORMS_LIST_PER_PAGE_OPTIONS,
  useFormsListQuery,
} from "../../../hooks/useFormsListQuery";
import { useFormListSelection } from "../../../hooks/useFormListSelection";
import { useFormsListPreferences } from "../../../hooks/useFormsListPreferences";
import { getFormListStats } from "../../../lib/admin/formListFilters";
import { isGlobalManager } from "../../../lib/forms/formSource";
import {
  bulkDeleteConfirmMessage,
  bulkOrganisationClearConfirmMessage,
  bulkOrganisationConfirmMessage,
  canManageListForm,
  runBulkFormAction,
  runBulkOrganisationChange,
  type BulkFormAction,
} from "../../../lib/admin/bulkFormActions";
import { AdminShell } from "../_components/AdminShell";
import { FormListRow } from "./FormListRow";
import { FormsListBulkBar } from "./FormsListBulkBar";
import { FormsListPagination } from "./FormsListPagination";
import { FormsListSortHeader } from "./FormsListSortHeader";
import { FormsListToolbar } from "./FormsListToolbar";
import { YandexSyncButton } from "./YandexSyncButton";
import type { FormListItemDto } from "../../../types/formList";

export default function FormsListPage() {
  const { roles, canCreateForms, canManageForm } = useAuth();
  const {
    organisations,
    selectedOrganisationId,
    selectedOrganisation,
    isUnassignedSelected,
    isGrantedSelected,
    isServiceFormsSelected,
    isLoadingOrganisations,
  } = useAdminWorkspace();
  const canSyncYandex = isGlobalManager(roles);
  const { preferences, updatePreferences, resetFilters } =
    useFormsListPreferences();
  const [page, setPage] = useState(1);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const selectAllRef = useRef<HTMLInputElement>(null);
  const selectedFormsByIdRef = useRef(new Map<number, FormListItemDto>());
  const search = useDebouncedValue(preferences.search, 300);

  const { forms, meta, isLoading, error, reload } = useFormsListQuery({
    organisationId: selectedOrganisationId,
    unassignedOnly: isUnassignedSelected,
    grantedOnly: isGrantedSelected,
    serviceOnly: isServiceFormsSelected,
    page,
    count: preferences.perPage,
    search,
    status: preferences.status,
    source: preferences.source,
    sortBy: preferences.sortBy,
    sortDir: preferences.sortDir,
  });

  const { selectedIds, selectedCount, toggle, selectMany, clear, isSelected } =
    useFormListSelection();

  const canManageFormItem = useCallback(
    (form: FormListItemDto) => canManageListForm(form, canManageForm, roles),
    [canManageForm, roles],
  );

  const organisationOptions = useMemo(
    () => organisations.map(({ id, name }) => ({ id, name })),
    [organisations],
  );
  const visiblePreferences = useMemo(
    () => ({
      ...preferences,
      organisationId: selectedOrganisationId,
    }),
    [preferences, selectedOrganisationId],
  );
  const stats = useMemo(() => getFormListStats(forms), [forms]);
  const pageForms = forms;

  const selectedFormsCache = selectedFormsByIdRef.current;
  for (const form of forms) {
    if (selectedIds.has(form.id)) selectedFormsCache.set(form.id, form);
  }
  for (const id of [...selectedFormsCache.keys()]) {
    if (!selectedIds.has(id)) selectedFormsCache.delete(id);
  }

  const manageablePageForms = useMemo(
    () => pageForms.filter(canManageFormItem),
    [pageForms, canManageFormItem],
  );

  const manageablePageIds = useMemo(
    () => manageablePageForms.map((form) => form.id),
    [manageablePageForms],
  );

  const allPageSelected =
    manageablePageIds.length > 0 &&
    manageablePageIds.every((id) => selectedIds.has(id));

  const somePageSelected =
    manageablePageIds.some((id) => selectedIds.has(id)) && !allPageSelected;

  const selectedForms = [...selectedIds]
    .map((id) => selectedFormsCache.get(id))
    .filter((form): form is FormListItemDto => form != null);

  const hasManageableForms =
    manageablePageForms.length > 0 || selectedCount > 0;

  const hasActiveFilters =
    preferences.search.trim() !== "" ||
    preferences.status !== "all" ||
    preferences.source !== "all";
  const listTotal = meta?.total ?? forms.length;
  const hasWorkspaceSelection =
    selectedOrganisationId != null ||
    isUnassignedSelected ||
    isGrantedSelected ||
    isServiceFormsSelected;

  useEffect(() => {
    document.title = "Управление формами";
  }, []);

  useEffect(() => {
    setPage(1);
    clear();
  }, [
    preferences.perPage,
    preferences.sortBy,
    preferences.sortDir,
    search,
    preferences.status,
    preferences.source,
    selectedOrganisationId,
    isUnassignedSelected,
    isGrantedSelected,
    isServiceFormsSelected,
    clear,
  ]);

  useEffect(() => {
    const input = selectAllRef.current;
    if (!input) return;
    input.indeterminate = somePageSelected;
  }, [somePageSelected, allPageSelected]);

  const handleToggleSelectAllPage = () => {
    if (allPageSelected) {
      const next = new Set(selectedIds);
      for (const id of manageablePageIds) next.delete(id);
      selectMany([...next]);
      return;
    }
    selectMany([...new Set([...selectedIds, ...manageablePageIds])]);
  };

  const handleBulkAction = async (action: BulkFormAction) => {
    const targets = selectedForms.filter(canManageFormItem);
    if (targets.length === 0) return;

    if (action === "delete") {
      if (!window.confirm(bulkDeleteConfirmMessage(targets.length))) return;
    }

    setActionError(null);
    setBulkRunning(true);

    try {
      const result = await runBulkFormAction(targets, action);
      await reload();
      clear();

      if (result.failed.length > 0) {
        const preview = result.failed
          .slice(0, 3)
          .map((item) => `«${item.name}»: ${item.message}`)
          .join("; ");
        const suffix =
          result.failed.length > 3 ? ` и ещё ${result.failed.length - 3}` : "";
        setActionError(
          `Выполнено для ${result.succeeded} из ${targets.length}. Ошибки: ${preview}${suffix}`,
        );
      }
    } finally {
      setBulkRunning(false);
    }
  };

  const handleBulkOrganisationChange = async (
    organisationId: number | null,
  ) => {
    const targets = selectedForms.filter(canManageFormItem);
    if (targets.length === 0) return;

    const organisationLabel =
      organisationId == null
        ? "Без организации"
        : (organisationOptions.find((org) => org.id === organisationId)?.name ??
          `#${organisationId}`);

    const confirmed = window.confirm(
      organisationId == null
        ? bulkOrganisationClearConfirmMessage(targets.length)
        : bulkOrganisationConfirmMessage(targets.length, organisationLabel),
    );
    if (!confirmed) return;

    setActionError(null);
    setBulkRunning(true);

    try {
      const result = await runBulkOrganisationChange(targets, organisationId);
      await reload();
      clear();

      if (result.failed.length > 0) {
        const preview = result.failed
          .slice(0, 3)
          .map((item) => `«${item.name}»: ${item.message}`)
          .join("; ");
        const suffix =
          result.failed.length > 3 ? ` и ещё ${result.failed.length - 3}` : "";
        setActionError(
          `Выполнено для ${result.succeeded} из ${targets.length}. Ошибки: ${preview}${suffix}`,
        );
      }
    } finally {
      setBulkRunning(false);
    }
  };

  useEffect(() => {
    const lastPage = Math.max(1, meta?.last_page ?? 1);
    if (page > lastPage) setPage(lastPage);
  }, [page, meta?.last_page]);

  return (
    <AdminShell
      compact
      fluid
      title="Формы"
      subtitle={
        isServiceFormsSelected
          ? "Сервисные формы"
          : isGrantedSelected
            ? "По гранту"
            : isUnassignedSelected
              ? "Остальные формы"
              : selectedOrganisation?.name
      }
      actions={
        <div className="flex items-center gap-2">
          {canSyncYandex ? (
            <YandexSyncButton onSynced={reload} onError={setActionError} />
          ) : null}
          {canCreateForms && hasWorkspaceSelection && !isGrantedSelected ? (
            <Link to="/admin/forms/new">
              <Button className="px-4 py-2 text-sm">
                <IconPlus size={15} />
                Создать
              </Button>
            </Link>
          ) : null}
        </div>
      }
    >
      {error ? (
        <div
          className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2"
          role="alert"
        >
          <p className="font-onest text-sm text-red-700">{error}</p>
        </div>
      ) : null}

      {actionError ? (
        <div
          className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2"
          role="alert"
        >
          <p className="font-onest text-sm text-red-700">{actionError}</p>
        </div>
      ) : null}

      {!hasWorkspaceSelection && !isLoadingOrganisations ? (
        <div className="rounded-xl border border-slate-200/80 bg-white px-4 py-12 text-center">
          <p className="font-onest text-sm text-slate-600">
            Нет доступных организаций
          </p>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200/80 bg-white">
          <div className="relative z-30">
            <FormsListToolbar
              preferences={visiblePreferences}
              totalCount={meta?.total}
              pageStats={stats}
              onSearchChange={(nextSearch) =>
                updatePreferences({ search: nextSearch })
              }
              onStatusChange={(status) => updatePreferences({ status })}
              onSourceChange={(source) => updatePreferences({ source })}
              onSortChange={(sortBy, sortDir) =>
                updatePreferences({ sortBy, sortDir })
              }
              onResetFilters={resetFilters}
            />
          </div>

          <div className="relative z-0 overflow-hidden rounded-b-xl">
            <FormsListBulkBar
              selectedCount={selectedCount}
              isRunning={bulkRunning}
              organisationOptions={organisationOptions}
              allowEmptyOrganisation={canSyncYandex}
              onAction={handleBulkAction}
              onChangeOrganisation={handleBulkOrganisationChange}
              onClear={clear}
            />

            {isLoading ? (
              <div className="divide-y divide-slate-100">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-2">
                    <div className="h-3 w-3 rounded-full bg-slate-100" />
                    <div className="h-3.5 flex-1 animate-pulse rounded bg-slate-100" />
                    <div className="h-3 w-8 animate-pulse rounded bg-slate-100" />
                  </div>
                ))}
              </div>
            ) : listTotal === 0 && hasActiveFilters ? (
              <div className="px-4 py-10 text-center">
                <p className="font-onest text-sm text-slate-600">
                  Ничего не найдено
                </p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-2 font-onest text-sm font-medium text-brand-600 hover:text-brand-500"
                >
                  Сбросить фильтры
                </button>
              </div>
            ) : listTotal === 0 ? (
              <div className="px-4 py-12 text-center">
                <p className="font-onest text-sm text-slate-600">
                  Форм пока нет
                </p>
                {canCreateForms && !isGrantedSelected ? (
                  <Link
                    to="/admin/forms/new"
                    className="mt-3 inline-flex items-center gap-1.5 font-onest text-sm font-medium text-brand-600 hover:text-brand-500"
                  >
                    <IconPlus size={15} />
                    Создать первую форму
                  </Link>
                ) : null}
              </div>
            ) : (
              <div className="md:overflow-x-auto">
                <table className="w-full table-fixed border-collapse md:table-auto">
                  <colgroup>
                    {hasManageableForms ? <col className="w-8" /> : null}
                    <col />
                    <col className="w-36" />
                    <col className="hidden w-24 md:table-column" />
                    <col className="w-10" />
                  </colgroup>
                  <thead className="hidden border-b border-slate-100 md:table-header-group">
                    <tr>
                      {hasManageableForms ? (
                        <th className="w-8 px-2 py-1.5 pl-3">
                          <input
                            ref={selectAllRef}
                            type="checkbox"
                            checked={allPageSelected}
                            onChange={handleToggleSelectAllPage}
                            disabled={
                              manageablePageIds.length === 0 || bulkRunning
                            }
                            aria-label="Выбрать все формы на странице"
                            className="h-3.5 w-3.5 rounded border-slate-300 text-brand-500 focus:ring-brand-500/30"
                          />
                        </th>
                      ) : null}
                      <th className="py-1.5 pr-2 text-left font-onest text-[11px] font-medium leading-4 text-slate-400">
                        Форма
                      </th>
                      <th className="w-36 px-2 py-1.5 text-right font-onest text-[11px] font-medium leading-4 text-slate-400">
                        Количество ответов
                      </th>
                      <FormsListSortHeader
                        sortBy={preferences.sortBy}
                        sortDir={preferences.sortDir}
                        onSortChange={(sortBy, sortDir) =>
                          updatePreferences({ sortBy, sortDir })
                        }
                      />
                      <th className="w-10 px-2 py-1.5 pr-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {pageForms.map((form) => (
                      <FormListRow
                        key={form.id}
                        form={form}
                        onCopyError={setActionError}
                        showSelectionColumn={hasManageableForms}
                        selectable={
                          hasManageableForms && canManageFormItem(form)
                        }
                        selected={isSelected(form.id)}
                        onToggleSelect={() => toggle(form.id)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!isLoading && listTotal > 0 ? (
              <FormsListPagination
                meta={meta}
                page={page}
                perPage={preferences.perPage}
                perPageOptions={FORMS_LIST_PER_PAGE_OPTIONS}
                isLoading={isLoading}
                onPageChange={setPage}
                onPerPageChange={(perPage) => updatePreferences({ perPage })}
              />
            ) : null}
          </div>
        </div>
      )}
    </AdminShell>
  );
}
